import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setDropdownOpen(false);
  }, [location.pathname]);

  const showTransparent = isHome && !scrolled;

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        showTransparent
          ? 'bg-transparent'
          : 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
      }`}
    >
      <div className="px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          <Link
            to="/"
            className={`flex items-center gap-2 transition-colors duration-200 ${
              showTransparent
                ? 'text-background-50 hover:text-background-200'
                : 'text-foreground-900 hover:text-primary-600'
            }`}
          >
            <span className="font-heading text-2xl font-bold tracking-tight">
              Vigorbuy<span className={showTransparent ? 'text-primary-300' : 'text-primary-500'}>.</span>
            </span>
          </Link>

          <div className="hidden md:flex items-center gap-6">
            <Link
              to="/"
              className={`text-sm font-semibold transition-colors duration-200 whitespace-nowrap ${
                showTransparent
                  ? 'text-background-50 hover:text-background-200'
                  : 'text-foreground-700 hover:text-primary-600'
              } ${!isHome ? '' : ''}`}
            >
              Home
            </Link>

            <div className="relative">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className={`flex items-center gap-1.5 text-sm font-semibold transition-colors duration-200 whitespace-nowrap cursor-pointer ${
                  showTransparent
                    ? 'text-background-50 hover:text-background-200'
                    : 'text-foreground-700 hover:text-primary-600'
                }`}
              >
                <span>Categories</span>
                <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {dropdownOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setDropdownOpen(false)}></div>
                  <div className="absolute top-full left-0 mt-2 w-72 bg-background-50 border border-background-200/70 rounded-xl py-2 shadow-sm z-50 max-h-96 overflow-y-auto">
                    {categories.map((cat) => (
                      <a
                        key={cat.name}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="flex items-center gap-3 px-4 py-3 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors duration-150"
                        onClick={() => setDropdownOpen(false)}
                      >
                        <div className="w-8 h-8 flex-shrink-0 rounded-md overflow-hidden">
                          <img
                            src={cat.image}
                            alt={cat.name}
                            className="w-full h-full object-cover"
                            loading="lazy"
                          />
                        </div>
                        <span className="font-medium">{cat.name}</span>
                      </a>
                    ))}
                  </div>
                </>
              )}
            </div>

            <Link
              to="/guide/vigorbuy-spreadsheet-guide"
              className={`text-sm font-semibold transition-colors duration-200 whitespace-nowrap ${
                showTransparent
                  ? 'text-background-50 hover:text-background-200'
                  : 'text-foreground-700 hover:text-primary-600'
              }`}
            >
              Shopping Guide
            </Link>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className={`text-sm font-semibold transition-colors duration-200 whitespace-nowrap ${
                showTransparent
                  ? 'text-background-50 hover:text-background-200'
                  : 'text-foreground-700 hover:text-primary-600'
              }`}
            >
              Spreadsheet
            </a>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className={`inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer ${
                showTransparent
                  ? 'bg-background-50/20 text-background-50 border border-background-50/30 hover:bg-background-50/30'
                  : 'bg-primary-500 text-background-50 hover:bg-primary-600'
              }`}
            >
              Buy It Now
              <i className="ri-arrow-right-up-line text-sm"></i>
            </a>
          </div>

          <button
            className={`md:hidden w-10 h-10 flex items-center justify-center cursor-pointer transition-colors duration-200 ${
              showTransparent ? 'text-background-50' : 'text-foreground-700'
            }`}
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
          >
            <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70 px-4 py-5 max-h-[80vh] overflow-y-auto">
          <div className="flex flex-col gap-4">
            <Link
              to="/"
              className="flex items-center gap-3 px-3 py-2.5 text-sm font-semibold text-foreground-700 hover:bg-primary-50 rounded-lg transition-colors"
              onClick={() => setMobileOpen(false)}
            >
              <i className="ri-home-line text-base w-6 h-6 flex items-center justify-center"></i>
              Home
            </Link>

            <div>
              <p className="text-xs font-semibold text-foreground-500 uppercase tracking-wider px-3 mb-2">Categories</p>
              <div className="grid grid-cols-1 gap-1">
                {categories.map((cat) => (
                  <a
                    key={cat.name}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-3 px-3 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 rounded-lg transition-colors"
                    onClick={() => setMobileOpen(false)}
                  >
                    <div className="w-8 h-8 flex-shrink-0 rounded-md overflow-hidden">
                      <img
                        src={cat.image}
                        alt={cat.name}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>
                    <span className="font-medium">{cat.name}</span>
                  </a>
                ))}
              </div>
            </div>

            <div className="border-t border-background-200/70 pt-4 flex flex-col gap-2">
              <Link
                to="/guide/vigorbuy-spreadsheet-guide"
                className="flex items-center gap-3 px-3 py-2.5 text-sm font-semibold text-foreground-700 hover:bg-primary-50 rounded-lg transition-colors"
                onClick={() => setMobileOpen(false)}
              >
                <i className="ri-book-open-line text-base w-6 h-6 flex items-center justify-center"></i>
                Shopping Guide
              </Link>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center gap-3 px-3 py-2.5 text-sm font-semibold text-foreground-700 hover:bg-primary-50 rounded-lg transition-colors"
                onClick={() => setMobileOpen(false)}
              >
                <i className="ri-external-link-line text-base w-6 h-6 flex items-center justify-center"></i>
                Open Spreadsheet
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center justify-center gap-2 mt-1 px-5 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-all duration-200 whitespace-nowrap"
                onClick={() => setMobileOpen(false)}
              >
                Buy It Now
                <i className="ri-arrow-right-up-line text-sm"></i>
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}